// Cave84 — Shared WishlistButton component
// Used on WineAnalysisPage and WinesPage.
// Always allows adding even when the wine is already on the wishlist
// (different vintage, different lot, re-tracking after acquired/abandoned).

import { useState } from 'react';
import { useActiveWishlistEntries, useCreateWishlistEntry } from '@/hooks/useWishlist';
import { useIsOwner } from '@/stores/auth.store';
import type { WishlistPriority } from '@cave84/shared-types';

const PRIORITY_CLS: Record<WishlistPriority, string> = {
  high:   'bg-red-900/60 text-red-300 border-red-700',
  medium: 'bg-amber-900/50 text-amber-300 border-amber-700',
  low:    'bg-surface-3 text-text-muted border-border-medium',
};

export interface WishlistButtonProps {
  wineId: string;
  wineName?: string;
  defaultYear?: number;
  size?: 'sm' | 'xs';
}

export function WishlistButton({ wineId, wineName, defaultYear, size = 'sm' }: WishlistButtonProps) {
  const isOwner = useIsOwner();
  const { data: entries = [] } = useActiveWishlistEntries();
  const createMutation = useCreateWishlistEntry();

  const [open, setOpen] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [priority, setPriority] = useState<WishlistPriority>('medium');
  const [targetPrice, setTargetPrice] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [notes, setNotes] = useState('');
  const [vintageYear, setVintageYear] = useState(defaultYear ? String(defaultYear) : '');

  if (!isOwner) return null;

  const alreadyOnList = entries.some(e => e.wine_id === wineId);

  function handleOpen() {
    setPriority('medium');
    setTargetPrice('');
    setCurrency('EUR');
    setNotes('');
    setSaveError('');
    setVintageYear(defaultYear ? String(defaultYear) : '');
    setOpen(true);
  }

  async function handleAdd() {
    setSaveError('');
    try {
      await createMutation.mutateAsync({
        wine_id: wineId,
        priority,
        ...(vintageYear ? { vintage_year: parseInt(vintageYear, 10) } : {}),
        ...(targetPrice ? { target_price: parseFloat(targetPrice), target_currency: currency } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      });
      setOpen(false);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save — check your connection.');
    }
  }

  const isXs = size === 'xs';

  return (
    <>
      {alreadyOnList ? (
        <div className="inline-flex items-center gap-1">
          <span className={`inline-flex items-center gap-1 rounded-full border border-gold/40 text-gold ${isXs ? 'px-1.5 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs'}`}>
            <StarIcon filled className={isXs ? 'w-2.5 h-2.5' : 'w-3 h-3'} />
            {!isXs && 'On Wishlist'}
          </span>
          <button type="button" onClick={handleOpen} title="Add again (different vintage or lot)"
            className={`rounded border border-border-subtle text-text-muted hover:border-gold/40 hover:text-gold transition-colors ${isXs ? 'px-1 py-0.5 text-[11px]' : 'px-1.5 py-0.5 text-xs'}`}>
            +
          </button>
        </div>
      ) : (
        <button type="button" onClick={handleOpen}
          className={`inline-flex items-center gap-1 rounded-full border border-border-medium bg-surface-2 text-text-secondary hover:border-gold/40 hover:text-gold transition-colors ${isXs ? 'px-1.5 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs'}`}>
          <StarIcon filled={false} className={isXs ? 'w-2.5 h-2.5' : 'w-3 h-3'} />
          {isXs ? 'Wish' : 'Add to Wishlist'}
        </button>
      )}

      {open && (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- click-outside backdrop; inner dialog holds the real controls
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
          onMouseDown={e => { if (e.target === e.currentTarget) setOpen(false); }}>
          <div className="bg-surface-1 border border-border-medium rounded-xl w-full max-w-sm shadow-2xl">
            <div className="flex items-start justify-between px-5 py-4 border-b border-border-subtle">
              <div>
                <h2 className="text-sm font-semibold text-text-primary">Add to Wishlist</h2>
                {wineName && <p className="text-xs text-text-muted mt-0.5 leading-snug">{wineName}</p>}
                {alreadyOnList && <p className="text-xs text-amber-400 mt-0.5">Already on your list — adding again</p>}
              </div>
              <button type="button" onClick={() => setOpen(false)} className="text-text-muted hover:text-text-primary ml-3 flex-shrink-0 mt-0.5">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="px-5 py-4 space-y-3">
              <div>
                <span className="block text-xs text-text-muted mb-1">Vintage year (optional)</span>
                <input type="number" value={vintageYear} onChange={e => setVintageYear(e.target.value)}
                  placeholder="Any vintage" min={1971} max={new Date().getFullYear()}
                  className="w-full px-3 py-2 rounded-md border border-border-medium bg-surface-0 text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gold text-sm" />
              </div>

              <div className="flex gap-3">
                <div className="flex-1">
                  <span className="block text-xs text-text-muted mb-1">Target price (optional)</span>
                  <input type="number" value={targetPrice} onChange={e => setTargetPrice(e.target.value)}
                    placeholder="e.g. 250" min={0}
                    className="w-full px-3 py-2 rounded-md border border-border-medium bg-surface-0 text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gold text-sm" />
                </div>
                <div className="w-20">
                  <span className="block text-xs text-text-muted mb-1">Currency</span>
                  <select value={currency} onChange={e => setCurrency(e.target.value)}
                    className="w-full px-2 py-2 rounded-md border border-border-medium bg-surface-0 text-text-primary focus:outline-none focus:border-gold text-sm">
                    <option>EUR</option><option>USD</option><option>ILS</option><option>GBP</option>
                  </select>
                </div>
              </div>

              <div>
                <span className="block text-xs text-text-muted mb-1">Priority</span>
                <div className="flex gap-2">
                  {(['high', 'medium', 'low'] as WishlistPriority[]).map(p => (
                    <button key={p} type="button" onClick={() => setPriority(p)}
                      className={`flex-1 py-1.5 rounded border text-xs font-medium capitalize transition-colors ${priority === p ? PRIORITY_CLS[p] : 'border-border-subtle bg-surface-2 text-text-muted hover:border-border-medium'}`}>
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <span className="block text-xs text-text-muted mb-1">Notes (optional)</span>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
                  placeholder="e.g. Only magnum, next MWC catalog..."
                  className="w-full px-3 py-2 rounded-md border border-border-medium bg-surface-0 text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gold text-sm resize-none" />
              </div>

              {saveError && (
                <p className="text-xs text-red-400 bg-red-900/20 border border-red-800 rounded px-3 py-2">{saveError}</p>
              )}

              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setOpen(false)}
                  className="flex-1 py-2 rounded-lg border border-border-medium bg-surface-2 text-text-secondary text-sm hover:border-border-strong transition-colors">
                  Cancel
                </button>
                <button type="button" disabled={createMutation.isPending} onClick={handleAdd}
                  className="flex-1 py-2 rounded-lg bg-accent-burgundy text-white text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-40">
                  {createMutation.isPending ? 'Adding...' : 'Add'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function StarIcon({ filled, className }: { filled: boolean; className?: string }) {
  const path = "M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z";
  return filled ? (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24"><path d={path} /></svg>
  ) : (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d={path} />
    </svg>
  );
}
